import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { UdtService } from 'src/app/shared/services/udt.service';
import { LoadCaseService } from '../../services/load-case.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { Store } from '@ngneat/elf';
import { PressTempUi } from '../../models/perivis-results-state.model';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';
import { toCamelCaseString } from 'src/app/shared/helpers/stringToCamel.helper';

type PressTempGridColumn = {
  header: string;
  field: string;
  valueFormatter(params: any): string;
};

type PressTempGridRow = Record<string, number | null>;

type PressTempLoadCaseSeries = {
  name: string;
  key: string;
  points: any[];
};

type ViewToggleEvent = {
  value?: string;
};

@Component({
  selector: 'app-pressures-temperatures',
  templateUrl: './pressures-temperatures.component.html',
  standalone: false
})
export class PressuresTemperaturesComponent implements OnInit, OnDestroy {

  public plot = {
    data: [] as any[]
  };

  public errorMsg: string | null = null;
  public yAxisTitle = '';
  public xAxisTitle = '';
  public plotName = '';
  public downloadPlotName = 'press_temp_plot';
  public isLoading = signal<boolean>(true);
  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];
  public resultsView: SelectItem[] = [
    { label: 'IPP', value: 'internalPressurePoints' },
    { label: 'EPP', value: 'externalPressurePoints' },
    { label: 'Temp', value: 'temperaturePoints' }
  ];

  private data: any[] = [];
  private udtData: any;
  private depthUnit = '';
  private temperatureUnit = '';
  private pressureUnit = '';
  private selectedString = '';
  public gridResults: PressTempGridRow[] = [];
  public gridColumnDefinitions: PressTempGridColumn[] = [];

  public depthView = [
    { label: 'TVD', value: 'tvd' },
    { label: 'MD', value: 'md' }
  ];

  //State Management
  private _componentId = '';
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.pressTempStore = this._storeService.createStore(this.componentId, new PressTempUi, withUIEntities<XyLinePlotUi>());
    this.selectedResultsView = this.pressTempStore.state.selectedResultsView || 'internalPressurePoints';
    this.selectedDepthView = this.pressTempStore.state.selectedDepthView || 'tvd';
    this.selectedResultDisplay = this.pressTempStore.state.selectedResultDisplay || 'plot';
  }
  get componentId(): string {
    return this._componentId;
  }
  public pressTempStore!: Store;

  public selectedResultsView: string = 'internalPressurePoints';
  public selectedDepthView: string = 'tvd';
  public selectedResultDisplay: string = 'plot';

  constructor(
    private _loadCaseService: LoadCaseService,
    private _signalRService: SignalRService,
    private _udtService: UdtService,
    private _storeService: StoreService
  ) { }

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);

    const units = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.depthUnit = units.longLengths;
    this.temperatureUnit = units.temperature;
    this.pressureUnit = unitsLib[units.pressure].symbol;

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds.tubularId;
    this.getData();
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS ||
      data.action === PeriforOnChangeMessages.REFRESH_PACKERS ||
      data.action == PeriforOnChangeMessages.REFRESH_LOAD_CASE_INPUTS ||
      data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.isLoading.set(true);
      const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      this.getData();
    }
  }

  setPlotName(): string {
    return 'pressuresTemperaturesPlot - ' + this.selectedString + ' - ' + this.selectedResultsView + ' - ' + this.selectedDepthView;
  }

  get depthColumnLabel(): string {
    return this.selectedDepthView === 'md' ? `(${this.depthUnit} MD)` : `(${this.depthUnit} TVD)`;
  }

  get valueColumnLabel(): string {
    return this.selectedResultsView === 'temperaturePoints'
      ? `Temperature (${unitsLib[this.temperatureUnit].symbol})`
      : `Pressure (${this.pressureUnit})`;
  }

  onSelectedResultDisplayUpdated(e: ViewToggleEvent): void {
    const value = e.value ?? this.selectedResultDisplay;
    this.selectedResultDisplay = value;
    this.pressTempStore.update(state => ({ ...state, selectedResultDisplay: value }));
  }

  resultsViewToggle(e: ViewToggleEvent): void {
    const value = e.value ?? this.selectedResultsView;
    this.selectedResultsView = value;
    this.pressTempStore.update(state => ({ ...state, selectedResultsView: value }));
    if (this.selectedDepthView == 'md' && this.selectedResultsView == 'temperaturePoints') {
      this.selectedDepthView = 'tvd';
      this.pressTempStore.update(state => ({ ...state, selectedDepthView: 'tvd' }));
    }
    this.plotName = this.setPlotName();
    this.refreshResults();
  }

  private getData() {
    this.errorMsg = null;
    const sources: Observable<any>[] = [
      this._loadCaseService.getAllPressuresAndTemperatures() as Observable<any>,
      this._udtService.getUndisturbedTemperaturePlot() as Observable<any>,
    ];

    forkJoin(sources).pipe(
      map(([pressTemp, udt]) => {
        this.data = pressTemp ?? [];
        this.udtData = udt;
        this.plotName = this.setPlotName();
        this.refreshResults();
      }),
      catchError(() => {
        this.isLoading.set(false);
        this.gridResults = [];
        this.gridColumnDefinitions = [];
        this.errorMsg = ' Failed to load input pressures and temperatures. Make sure PERICAL operations have been calculated if PERICAL Link load cases have been specified';
        return of(null);
      })).subscribe();

  }

  private refreshResults(): void {
    this.plotData();
    this.buildGridData();
  }

  plotData() {
    this.plot.data = [];

    let count = 0;
    for (let i = 0; i < this.data.length; i++) {
      const numPoints = this.data[i]?.[this.selectedResultsView].length ?? null;
      const xArray = new Array(numPoints);

      if (this.data[i] === null) {
        const defaultPlot = {
          x: 0,
          y: 0
        }

        this.plot.data.push(defaultPlot);

      } else {
        const plotData = this.data[i]?.[this.selectedResultsView];

        const yArray = new Array(numPoints);

        for (let i = 0; i < numPoints; i++) {
          const value = this.selectedResultsView == 'temperaturePoints' ? plotData[i].temperature : plotData[i].pressure;
          xArray[i] = (Math.trunc(value * 100) / 100);
          const depth = this.selectedDepthView == 'md' ? plotData[i].measuredDepth : plotData[i].trueVerticalDepth;
          yArray[i] = (Math.trunc(depth * 100) / 100);
        }

        const lineTypes = ['dot', 'dash', 'longdash', 'dashdot', 'longdashdot'];
        const trace = {
          name: this.data[i].name,
          x: xArray,
          y: yArray,
          mode: 'lines',
          line: {
            dash: this.data.length > 8 ? lineTypes[(count - 9) % lineTypes.length] : 'solid'
          }
        }
        count++;

        this.plot.data.push(trace);

        if (this.selectedResultsView === 'temperaturePoints' && i == this.data.length - 1) {
          const udt = {
            name: 'UDT',
            x: this.udtData.x,
            y: this.udtData.y,
            mode: 'lines',
            line: {
              dash: 'dot',
              color: '#FF0000',
              width: 1.5
            }
          }

          this.plot.data.push(udt);
        }
      }
    }

    this.yAxisTitle = this.selectedDepthView === 'md' ? `MD (${this.depthUnit})` : `TVD (${this.depthUnit})`
    this.xAxisTitle = this.selectedResultsView === 'temperaturePoints'
      ? `Temperature (${unitsLib[this.temperatureUnit].symbol})`
      : `Pressure (${this.pressureUnit})`;

    this.isLoading.set(false);
  }

  private buildGridData(): void {
    const series = this.getLoadCaseSeries();
    const maxRows = series.reduce((max, item) => Math.max(max, item.points.length), 0);

    this.gridColumnDefinitions = [];
    series.forEach((seriesItem, index) => {
      const fieldPrefix = seriesItem.key || `loadCase${index}`;

      this.gridColumnDefinitions.push(
        {
          header: `${seriesItem.name} ${this.depthColumnLabel}`,
          field: `${fieldPrefix}Depth`,
          valueFormatter: (params: any) => this.formatDecimal(params, 100)
        },
        {
          header: `${seriesItem.name} ${this.valueColumnLabel}`,
          field: `${fieldPrefix}Value`,
          valueFormatter: (params: any) => this.formatDecimal(params, 100)
        }
      );
    });

    this.gridResults = Array.from({ length: maxRows }, (_, rowIndex) => {
      const row: PressTempGridRow = {};

      series.forEach((seriesItem, index) => {
        const fieldPrefix = seriesItem.key || `loadCase${index}`;
        const point = seriesItem.points[rowIndex];
        row[`${fieldPrefix}Depth`] = this.getDepthValue(point);
        row[`${fieldPrefix}Value`] = this.getResultValue(point);
      });

      return row;
    });
  }

  private getLoadCaseSeries(): PressTempLoadCaseSeries[] {
    return (this.data ?? [])
      .filter(loadCase => loadCase != null)
      .map((loadCase, index) => {
        const name = loadCase?.name ?? `Load Case ${index + 1}`;
        return {
          name,
          key: `${toCamelCaseString(name)}${index}`,
          points: loadCase?.[this.selectedResultsView] ?? []
        };
      });
  }

  private getResultValue(point: any): number | null {
    if (!point) {
      return null;
    }

    return this.selectedResultsView === 'temperaturePoints' ? point.temperature : point.pressure;
  }

  private getDepthValue(point: any): number | null {
    if (!point) {
      return null;
    }

    return this.selectedDepthView === 'md' ? point.measuredDepth : point.trueVerticalDepth;
  }

  private formatDecimal(value: any, divisor: number): string {
    return value || value === 0 ? (Math.trunc(value * divisor) / divisor).toLocaleString('en-US') : '';
  }

  public depthViewToggle(e: ViewToggleEvent): void {
    const value = e.value ?? this.selectedDepthView;
    this.selectedDepthView = value;
    this.pressTempStore.update(state => ({ ...state, selectedDepthView: value }));
    this.plotName = this.setPlotName();
    this.refreshResults();
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}
